import React, { useEffect, useState, memo, useMemo, useCallback } from "react";
import { Image, StyleProp, ImageStyle, Platform, View } from "react-native";
import { useDispatch, useSelector } from "react-redux";
import styles from "../../stylesheet";
import { store, DefaultRootState } from "@reduxLocal/persistState";
import { debounce } from "lodash";
import { SHOW_AUTH_MODAL } from "@reduxLocal/actions/types";
import UserIcon from "@assets/icons/UserIcon";

type AvatarImageProps = {
  style?: StyleProp<ImageStyle>;
  image?: string;
  withShadow?: boolean;
};

type UserData = {
  id?: string;
  avatar?: {
    img?: string;
  };
};

// Utility function to generate hash (no global prototype modification)
const generateHash = (...args: any[]): number => {
  const str = args.join('-');
  let hash = 0;
  if (str.length === 0) return hash;
  
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash = hash & hash; // Convert to 32-bit integer
  }
  
  return Math.abs(hash) || 0;
};

// Default avatar URL constant
const DEFAULT_AVATAR_URL = "https://storage.cloud.google.com/smartsaas-avatar-store/Asset%202.png";

// URL correction utility
const correctStorageUrl = (url: string): string => {
  if (!url) return DEFAULT_AVATAR_URL;
  return url.replace(
    'https://storage.googleapis.com/smartsaas-avatar-store/',
    'https://storage.cloud.google.com/smartsaas-avatar-store/'
  );
};

/**
 * Optimized Avatar Image Component
 * 
 * Features:
 * - Automatic URL correction for Google Cloud Storage
 * - Cache busting with refresh counter
 * - Error handling with fallback to default avatar
 * - Memory leak prevention with proper cleanup
 * - Performance optimizations with custom memo comparison
 */
const AvatarImage = ({ style, image, withShadow }: AvatarImageProps) => {
  const user = useSelector((state: DefaultRootState) => state.promiseData?.data?.userData);
  const [refreshCounter, setRefreshCounter] = useState(0);
  const [avatarUrl, setAvatarUrl] = useState<string>(() => 
    correctStorageUrl(user?.avatar?.img) || DEFAULT_AVATAR_URL
  );
  const dispatch = useDispatch();

  // Debounced counter update for avatar refresh (reduced from 2000ms to 1000ms)
  const updateCounter = useMemo(
    () => debounce(() => setRefreshCounter(prev => prev + 1), 1000),
    []
  );

  // Listen for avatar updates
  useEffect(() => {
    const unsubscribe = store.subscribe(() => {
      const action = store.getState().lastAction;
      if (action?.type === 'AVATAR UPDATED SUCCESSFULLY') {
        updateCounter();
      }
    });

    return unsubscribe;
  }, [updateCounter]);

  // Update avatar URL when user data changes
  useEffect(() => {
    const newUrl = correctStorageUrl(user?.avatar?.img) || DEFAULT_AVATAR_URL;
    setAvatarUrl(newUrl);
  }, [user?.avatar?.img]);

  // Generate cache-busting image URI
  const imageUri = useMemo(() => {
    const hash = generateHash(avatarUrl, refreshCounter);
    return `${avatarUrl}?v=${hash}`;
  }, [avatarUrl, refreshCounter]);

  // Handle image loading errors
  const handleImageError = useCallback(() => {
    const currentUrl = user?.avatar?.img;
    
    if (!currentUrl) {
      setAvatarUrl(DEFAULT_AVATAR_URL);
      return;
    }

    // If already using the correct domain, show error
    if (currentUrl.includes('https://storage.cloud.google.com')) {
      dispatch({ 
        type: SHOW_AUTH_MODAL, 
        payload: { 
          modalType: 'error', 
          modalContent: 'Error loading avatar image' 
        } 
      });
    } else {
      // Try correcting the URL
      const correctedUrl = correctStorageUrl(currentUrl);
      setAvatarUrl(correctedUrl);
    }
  }, [user?.avatar?.img, dispatch]);

  // Platform-specific shadow and borderRadius
  // Split web and native styles to avoid type errors
  const nativeShadowStyle = Platform.select({
    ios: withShadow
      ? {
          shadowRadius: 20,
          shadowOpacity: 0.2,
          shadowColor: '#000',
          shadowOffset: { width: 0, height: 4 },
        }
      : {},
    android: withShadow
      ? {
          // elevation is not a valid ImageStyle, so skip for Image
        }
      : {},
  });

  const avatarStyle: StyleProp<ImageStyle> = [
    styles.bigAvatar,
    {
      borderRadius: 30,
      aspectRatio: 1,
      minWidth: 40,
      minHeight: 40,
    },
    nativeShadowStyle,
    style,
  ];

  // On web, wrap Image in a View for shadow
  if (Platform.OS === 'web') {
    const webShadowStyle = withShadow
      ? {
          boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
          WebkitBoxShadow: '0 4px 16px rgba(0,0,0,0.15)',
          borderRadius: 30,
        }
      : { borderRadius: 30 };
    return (
      <View style={webShadowStyle}>
        <Image
          key={`avatar-${user?.id || 'default'}-${refreshCounter}`}
          onError={handleImageError}
          source={{ uri: image || imageUri }}
          style={avatarStyle}
          alt="User avatar"
        />
      </View>
    );
  }

  console.log("image is", image, imageUri)
  // On native, just use Image
  return avatarUrl ? (
    <Image
      key={`avatar-${user?.id || 'default'}-${refreshCounter}`}
      onError={handleImageError}
      source={{ uri: avatarUrl || imageUri }}
      style={avatarStyle}
    />
  ) : (
    <UserIcon style={[{ minHeight: 40, maxHeight: 40 }, style]} />
  )
};

export default memo(AvatarImage, (prevProps, nextProps) => {
  // Custom comparison for better performance
  return (
    prevProps.image === nextProps.image &&
    prevProps.withShadow === nextProps.withShadow &&
    JSON.stringify(prevProps.style) === JSON.stringify(nextProps.style)
  );
});
